import React, { useRef } from 'react';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import LazyImage from './LazyImage';

const DesktopSectionSlider = ({ title, items, linkTo, type = 'product' }) => {
  const navigate = useNavigate();
  const { resolveMediaUrl } = useSettings();
  const scrollContainerRef = useRef(null);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const formatPrice = (price) => {
    const n = Number(price);
    if (!Number.isFinite(n)) return '';
    return `${new Intl.NumberFormat('fa-IR').format(n)} تومان`;
  };

  const pickProductImage = (product) => {
    const candidates = [
      product?.main_image,
      product?.mainImage,
      product?.main_image_url,
      product?.mainImageUrl,
      product?.image,
      product?.thumbnail_url,
      product?.thumbnail,
      Array.isArray(product?.images)
        ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.image)
        : null,
    ];
    for (const c of candidates) {
      const resolved = resolveMediaUrl(c);
      if (resolved) return resolved;
    }
    return null;
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="mb-12 relative group">
      <div className="flex items-center justify-between mb-6 px-4">
        <h2 className="text-2xl font-bold text-gray-900 border-r-4 border-kadolar-600 pr-4">{title}</h2>
        {linkTo && (
          <button 
            onClick={() => navigate(linkTo)}
            className="text-kadolar-600 hover:text-kadolar-700 font-medium flex items-center gap-1 text-sm"
          >
            مشاهده همه
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="relative">
        <button 
            onClick={scrollRight}
            className="absolute -right-5 top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-100 text-gray-600 p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-kadolar-50 hover:text-kadolar-600"
            aria-label="Scroll Right"
        >
            <ChevronRightIcon className="w-6 h-6" />
        </button>
        <button 
            onClick={scrollLeft}
            className="absolute -left-5 top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-100 text-gray-600 p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-kadolar-50 hover:text-kadolar-600"
            aria-label="Scroll Left"
        >
            <ChevronLeftIcon className="w-6 h-6" />
        </button>

        <div 
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto pb-8 pt-2 px-4 scrollbar-hide snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
            {items.map((item, idx) => (
                <div 
                    key={item.id || idx} 
                    className="min-w-[220px] w-[220px] bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group/card snap-start flex flex-col cursor-pointer overflow-hidden"
                    onClick={() => navigate(`/product/${item.id}`)}
                >
                    <div className="aspect-[5/6] bg-gray-50 relative overflow-hidden">
                        <LazyImage 
                            src={pickProductImage(item)} 
                            alt={item.title} 
                            className="w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-500"
                        />
                        {/* Overlay Actions could go here */}
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                        <h3 className="text-gray-800 font-medium text-sm mb-2 line-clamp-2 min-h-[40px] leading-relaxed group-hover/card:text-kadolar-700 transition-colors">
                            {item.title}
                        </h3>
                        <div className="mt-auto pt-2 border-t border-gray-50 flex items-center justify-between">
                            {/* Price */}
                             <div className="font-bold text-lg" style={{ color: '#16a34a' }}>
                                {formatPrice(item.price)}
                             </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default DesktopSectionSlider;
